import { useQueryClient } from '@tanstack/react-query';
import {
  type CaptureResult, type Task, type TaskStatus, type Waiting, endPosition, inScope, positionFor, statusName, transition,
} from '@quadro/domain';
import * as api from '../../data/api';
import { keys, patchTaskInCache } from '../../data/queries';
import { UNDO_MS, hud } from '../../components/Hud';
import { scheduleDelete } from '../../data/pendingDeletes';
import { openSheet } from '../../state/sheets';
import { useUI } from '../../state/ui';
import { useWorkspace } from '../../state/workspace';
import { celebrate } from './effects';

/** Ações de tarefa usadas em todo o app (quadro, mapa, rotinas). */
export function useTaskActions() {
  const qc = useQueryClient();
  const ws = useWorkspace();
  const tasks = () => qc.getQueryData<Task[]>(keys.tasks) ?? [];
  const refresh = () => qc.invalidateQueries({ queryKey: keys.tasks });
  const fail = (e: unknown) => { hud((e as Error).message || 'Não foi possível salvar agora.'); refresh(); };

  /** Cria a partir da captura; herda projeto/tema/categoria/entidade do filtro atual. */
  async function createFromCapture(r: CaptureResult, status: TaskStatus, extra: Partial<Task> = {}): Promise<string | null> {
    if (!r.title || !ws.canWrite) return null;
    const { scope } = useUI.getState();
    const scopedProject = scope.project !== 'all' && scope.project !== 'mine' ? scope.project : null;
    const draft: Partial<Task> & { title: string } = {
      title: r.title, status,
      projectId: r.projectId ?? scopedProject,
      assigneeId: r.assigneeId ?? (scope.project === 'mine' ? ws.me.id : null),
      priority: r.priority, due: r.due,
      themeIds: r.themeIds.length ? r.themeIds : scope.theme !== 'all' ? [scope.theme] : [],
      classification: scope.category !== 'all' ? { categoryId: scope.category, subcategoryId: null } : null,
      entityIds: [...new Set([...r.entityIds, ...(extra.entityIds ?? []), ...(scope.entity !== 'all' && !extra.entityIds ? [scope.entity] : [])])],
      position: endPosition(tasks(), status),
      ...extra,
    };
    try {
      const id = await api.createTask(draft);
      await refresh();
      return id;
    } catch (e) { fail(e); return null; }
  }

  /** Volta a tarefa para a etapa e o lugar de antes (o "Desfazer" do aviso). */
  function undoMove(before: Task) {
    return async () => {
      const t = tasks().find(x => x.id === before.id);
      if (!t) return;
      patchTaskInCache(qc, before.id, { ...transition(t, before.status, new Date().toISOString()), position: before.position, waiting: before.waiting });
      hud('Movimento desfeito');
      try { await api.updateTask(before.id, { status: before.status, position: before.position, waiting: before.waiting }); await refresh(); }
      catch (e) { fail(e); }
    };
  }

  async function doMove(id: string, status: TaskStatus, beforeId: string | null, waiting?: Waiting) {
    const t = tasks().find(x => x.id === id);
    if (!t) return;
    const position = positionFor(tasks(), id, status, beforeId);
    const patch: Partial<Task> = { ...transition(t, status, new Date().toISOString()), position, ...(waiting ? { waiting } : {}) };
    patchTaskInCache(qc, id, patch);
    if (t.status !== status) {
      const undo = { label: 'Desfazer', run: undoMove(t) };
      if (status === 'done') { hud('Tarefa finalizada', { action: undo }); celebrate(id); }
      else {
        const n = status === 'doing' ? tasks().filter(x => x.status === 'doing' && inScope(x, useUI.getState().scope, ws.me.id)).length : 0;
        hud(n > ws.settings.wipLimit ? `Fazendo agora tem ${n} tarefas, acima do limite de ${ws.settings.wipLimit}` : `Movida para ${statusName(status)}`, { action: undo });
      }
    }
    try {
      await api.updateTask(id, { status, position, ...(status === 'waiting' ? { waiting: waiting ?? t.waiting } : {}) });
    } catch (e) { fail(e); }
  }

  /** Move pedindo "quem e quando cobrar" ao entrar em Aguardando. */
  function move(id: string, status: TaskStatus, beforeId: string | null = null) {
    const t = tasks().find(x => x.id === id);
    if (!t || !ws.canWrite) return;
    if (status === 'waiting' && t.status !== 'waiting') {
      openSheet({ kind: 'waiting', taskId: id, title: t.title, onConfirm: w => doMove(id, status, beforeId, w) });
      return;
    }
    doMove(id, status, beforeId);
  }

  async function save(id: string, patch: Partial<Task>, log?: string) {
    const t = tasks().find(x => x.id === id);
    const to = patch.status;
    if (t && to && to !== t.status) {
      patch = { ...patch, position: endPosition(tasks(), to) };
      patchTaskInCache(qc, id, { ...transition(t, to, new Date().toISOString()), ...patch });
      if (to === 'done') celebrate(id);
    }
    try { await api.updateTask(id, patch, log); await refresh(); qc.invalidateQueries({ queryKey: keys.history(id) }); }
    catch (e) { fail(e); }
  }

  /** Exclui com "Desfazer": some na hora; o servidor só apaga depois de alguns segundos. */
  function remove(id: string) {
    qc.setQueryData<Task[]>(keys.tasks, list => list?.filter(t => t.id !== id));
    const cancel = scheduleDelete(id, UNDO_MS, () => { api.deleteTask(id).catch(fail); });
    hud('Tarefa excluída', { action: { label: 'Desfazer', run: () => { if (cancel()) { refresh(); hud('Exclusão desfeita'); } } } });
  }

  return { createFromCapture, move, save, remove };
}
